import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  Pagination,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  CloudDownload,
  ExpandLess,
  ExpandMore,
  PlayArrow,
  Refresh,
  Stop,
} from "@mui/icons-material";
import { Link as RouterLink, Navigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { useApiClient } from "../../../api";
import { FruitifyContainer } from "../../../shared-components/Containers";
import SchoolSearchAutocomplete from "../../school/components/SchoolSearchAutocomplete";
import { useAuthenticationStore } from "../../../stores/auth";
import { usePagination } from "../../../hooks/usePagination";

import SaveEnrichmentSegmentDialog from "../bulletins/SaveEnrichmentSegmentDialog";

const ACTIVE = new Set(["pending", "processing"]);
const labels = {
  pending: "Pending",
  processing: "Processing",
  successful: "Contacts found",
  no_lead: "No contacts",
  failed: "Failed",
  cancelled: "Cancelled",
};
const inferenceWarningLabels = {
  missing_pattern_evidence: "No published email-pattern evidence was returned.",
  low_confidence: "The search provider reported low confidence.",
  email_pattern_mismatch: "The address does not match the reported pattern.",
  invalid_pattern_example_email: "A pattern example has an invalid email.",
  invalid_pattern_example_url: "A pattern example has an invalid source URL.",
  pattern_example_evidence_mismatch:
    "A pattern example is not supported by its evidence excerpt.",
  pattern_example_domain_mismatch:
    "A pattern example uses a different email domain.",
  pattern_example_pattern_mismatch:
    "A published example does not match the reported pattern.",
  insufficient_distinct_pattern_examples:
    "Fewer than two distinct published pattern examples were returned.",
};

export function inferenceWarningLabel(code) {
  return inferenceWarningLabels[code] || "Additional validation is required.";
}

function PageControl({ page, pageInfo, onChange }) {
  return (
    <Pagination
      count={pageInfo.hasNextPage ? page + 1 : page}
      page={page}
      onChange={onChange}
    />
  );
}

function ContactCard({ contact, busy, onEdit }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          gap={2}
        >
          <Box>
            <Typography variant="h6">
              {contact.firstName} {contact.lastName}
            </Typography>
            <Typography>{contact.jobTitle}</Typography>
            <Typography color="text.secondary">{contact.email}</Typography>
            <Stack direction="row" gap={1} mt={1} flexWrap="wrap">
              {contact.subjects.map((subject) => (
                <Chip key={subject.id} size="small" label={subject.name} />
              ))}
              <Chip
                size="small"
                color={
                  contact.discoveryType === "confirmed" ? "success" : "warning"
                }
                label={
                  contact.discoveryType === "confirmed"
                    ? "Publicly confirmed"
                    : `${contact.confidence} confidence inferred`
                }
              />
              <Chip
                size="small"
                variant="outlined"
                label={contact.verificationStatus}
              />
            </Stack>
            {contact.discoveryType === "inferred" && (
              <Alert
                severity={
                  contact.verificationStatus === "provider_verified"
                    ? "success"
                    : contact.verificationStatus === "provider_invalid"
                      ? "error"
                      : "warning"
                }
                sx={{ mt: 1 }}
              >
                <Typography variant="body2">
                  {contact.verificationStatus === "provider_verified"
                    ? "Validated by Hunter: this inferred address is eligible for outreach."
                    : contact.verificationStatus === "provider_invalid"
                      ? "Hunter reported this inferred address as invalid."
                      : "Pending validation: this inferred address is not verified and cannot be used for outreach."}
                </Typography>
                {contact.validationCheckedAt && (
                  <Typography variant="caption">
                    Checked{" "}
                    {new Date(contact.validationCheckedAt).toLocaleString()}
                    {contact.validationScore != null
                      ? ` · score ${contact.validationScore}`
                      : ""}
                  </Typography>
                )}
                {contact.evidenceMetadata?.inference_validation?.warnings
                  ?.length > 0 && (
                  <Box component="ul" sx={{ mb: 0, pl: 2.5 }}>
                    {contact.evidenceMetadata.inference_validation.warnings.map(
                      (warning) => (
                        <li key={warning}>{inferenceWarningLabel(warning)}</li>
                      ),
                    )}
                  </Box>
                )}
              </Alert>
            )}
            <Typography variant="body2" mt={1}>
              {contact.evidenceDetail}
            </Typography>
            {contact.evidenceMetadata?.inference_evidence?.pattern && (
              <Typography variant="body2" color="text.secondary">
                Pattern: {contact.evidenceMetadata.inference_evidence.pattern}
              </Typography>
            )}
            <Link href={contact.evidenceUrl} target="_blank" rel="noreferrer">
              View evidence
            </Link>
          </Box>
          <Stack alignItems="flex-end">
            <Button disabled={busy} onClick={onEdit}>
              Edit contact
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

function AttemptRow({
  attempt,
  api,
  busy,
  onAction,
  onEdit,
  onRefreshSummary,
}) {
  const [expanded, setExpanded] = useState(false);
  const fetchContacts = useCallback(
    ({ first, after }) =>
      api.getOutreachEnrichmentAttemptContacts(attempt.id, { first, after }),
    [api, attempt.id],
  );
  const contacts = usePagination(fetchContacts, 10, {}, expanded);

  useEffect(() => {
    const validationMayBeRunning = contacts.options.some(
      (contact) =>
        contact.discoveryType === "inferred" &&
        contact.verificationStatus === "unverified" &&
        (!contact.validationStatus ||
          ["queued", "pending"].includes(contact.validationStatus)),
    );
    if (!expanded || !validationMayBeRunning) return undefined;
    const timer = setInterval(() => {
      contacts.refetch({ silent: true });
      onRefreshSummary({ silent: true });
    }, 3000);
    return () => clearInterval(timer);
  }, [contacts.options, contacts.refetch, expanded, onRefreshSummary]);

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton
            size="small"
            aria-label={`${expanded ? "Collapse" : "Expand"} contacts for ${attempt.school.name}`}
            sx={{ border: 0, "&:hover": { border: 0 } }}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
          {attempt.school.name}
        </TableCell>
        <TableCell>{attempt.school.phaseOfEducationDisplay}</TableCell>
        <TableCell>
          <Chip size="small" label={labels[attempt.status] || attempt.status} />
        </TableCell>
        <TableCell>{attempt.publiclyConfirmedCount}</TableCell>
        <TableCell>{attempt.inferredVerifiedCount}</TableCell>
        <TableCell>{attempt.inferredUnverifiedCount}</TableCell>
        <TableCell>{attempt.inferredInvalidCount}</TableCell>
        <TableCell>
          <Chip
            size="small"
            color="primary"
            label={attempt.totalConfirmedCount}
          />
        </TableCell>
        <TableCell>
          {["failed", "no_lead"].includes(attempt.status) && (
            <Button
              startIcon={<Refresh />}
              disabled={busy}
              onClick={() =>
                onAction(
                  () => api.retryOutreachEnrichmentAttempt(attempt.id),
                  contacts.refetch,
                )
              }
            >
              Retry
            </Button>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={9} sx={{ py: expanded ? 2 : 0 }}>
          <Collapse in={expanded} unmountOnExit>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1">Contacts</Typography>
              {contacts.loading && <CircularProgress size={24} />}
              {contacts.error && (
                <Alert severity="error">{contacts.error.message}</Alert>
              )}
              {!contacts.loading && contacts.options.length === 0 && (
                <Alert severity="info">
                  No contacts are available for this school.
                </Alert>
              )}
              {contacts.options.map((contact) => (
                <ContactCard
                  key={contact.id}
                  contact={contact}
                  busy={busy}
                  onEdit={() => onEdit(contact, contacts.refetch)}
                />
              ))}
              {contacts.options.length > 0 && (
                <PageControl
                  {...contacts}
                  onChange={contacts.handlePageChange}
                />
              )}
            </Stack>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function BatchCard({
  batch,
  api,
  busy,
  onAction,
  onEdit,
  onRunAgain,
  refreshBatches,
}) {
  const [expanded, setExpanded] = useState(false);
  const [savingSegment, setSavingSegment] = useState(false);
  const fetchAttempts = useCallback(
    ({ first, after }) =>
      api.getOutreachEnrichmentAttempts(batch.id, { first, after }),
    [api, batch.id],
  );
  const attempts = usePagination(fetchAttempts, 10, {}, expanded);

  useEffect(() => {
    const validationStatus = batch.validationJob?.status
      ?.toLowerCase()
      .replaceAll(" ", "_");
    const validationActive = ["pending", "in_progress"].includes(
      validationStatus,
    );
    if (!expanded || (!ACTIVE.has(batch.status) && !validationActive))
      return undefined;
    const timer = setInterval(() => {
      attempts.refetch({ silent: true });
      refreshBatches({ silent: true });
    }, 5000);
    return () => clearInterval(timer);
  }, [
    batch.status,
    batch.validationJob?.status,
    expanded,
    attempts.refetch,
    refreshBatches,
  ]);

  return (
    <Card variant="outlined">
      {savingSegment && (
        <SaveEnrichmentSegmentDialog
          key={batch.id}
          batch={batch}
          api={api}
          onClose={() => setSavingSegment(false)}
        />
      )}
      <CardContent>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          gap={2}
        >
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <IconButton
              aria-label={`${expanded ? "Collapse" : "Expand"} enrichment batch`}
              sx={{ border: 0, "&:hover": { border: 0 } }}
              onClick={() => setExpanded((value) => !value)}
            >
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
            <Box>
              <Typography variant="h6">
                {new Date(batch.requestedAt).toLocaleString()}
              </Typography>
              <Stack direction="row" gap={1} flexWrap="wrap" mt={0.5}>
                <Chip size="small" label={batch.status} />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${batch.counts?.successful || 0} successful`}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${batch.counts?.no_lead || 0} no contacts`}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${batch.counts?.failed || 0} failed`}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary" mt={1}>
                Subjects:{" "}
                {batch.subjects.map((subject) => subject.name).join(", ") ||
                  "None"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Object.values(batch.counts || {}).reduce(
                  (total, value) => total + value,
                  0,
                )}{" "}
                schools · limit {batch.batchLimit} ·{" "}
                {batch.searchCallCount || 0} web searches · $
                {Number(batch.estimatedCostUsd || 0).toFixed(4)} estimated
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Automatic email validation:{" "}
                {batch.attemptEmailValidation ? "On" : "Off"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Started:{" "}
                {batch.startedAt
                  ? new Date(batch.startedAt).toLocaleString()
                  : "Not started"}{" "}
                · Finished:{" "}
                {batch.completedAt || batch.cancelledAt
                  ? new Date(
                      batch.completedAt || batch.cancelledAt,
                    ).toLocaleString()
                  : "Not finished"}
              </Typography>
              {batch.job?.message && (
                <Typography variant="body2">{batch.job.message}</Typography>
              )}
              {batch.validationJob && (
                <Typography variant="body2" color="text.secondary">
                  Email validation: {batch.validationJob.status} ·{" "}
                  {batch.validationJob.progress || 0}%
                  {batch.validationJob.message
                    ? ` · ${batch.validationJob.message}`
                    : ""}
                </Typography>
              )}
            </Box>
          </Stack>
          <Stack alignItems={{ md: "flex-end" }} spacing={1}>
            <Button disabled={busy} onClick={() => setSavingSegment(true)}>
              Save to segment
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              disabled={busy}
              onClick={() => onRunAgain(batch)}
            >
              Run again
            </Button>
            {ACTIVE.has(batch.status) && (
              <Button
                color="error"
                startIcon={<Stop />}
                disabled={busy}
                onClick={() =>
                  onAction(
                    () => api.cancelOutreachEnrichment(batch.id),
                    attempts.refetch,
                  )
                }
              >
                Cancel batch
              </Button>
            )}
          </Stack>
        </Stack>
        <Collapse in={expanded} unmountOnExit>
          <Box mt={2}>
            {attempts.loading && <CircularProgress size={24} />}
            {attempts.error && (
              <Alert severity="error">{attempts.error.message}</Alert>
            )}
            {!attempts.loading && attempts.options.length === 0 ? (
              <Alert severity="info">
                No schools are recorded for this batch.
              </Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>School</TableCell>
                    <TableCell>Phase</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Public</TableCell>
                    <TableCell>Inferred verified</TableCell>
                    <TableCell>Inferred pending</TableCell>
                    <TableCell>Inferred invalid</TableCell>
                    <TableCell>Total confirmed</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attempts.options.map((attempt) => (
                    <AttemptRow
                      key={attempt.id}
                      attempt={attempt}
                      api={api}
                      busy={busy}
                      onAction={onAction}
                      onEdit={onEdit}
                      onRefreshSummary={attempts.refetch}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
            {attempts.options.length > 0 && (
              <Box mt={2}>
                <PageControl
                  {...attempts}
                  onChange={attempts.handlePageChange}
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

function ContactDialog({ contact, subjects, onClose, onSave }) {
  const [value, setValue] = useState(() => ({
    firstName: contact.firstName,
    lastName: contact.lastName,
    jobTitle: contact.jobTitle,
    subjectIds: contact.subjects.map((item) => item.id),
  }));
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit outreach contact</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Alert severity="info">
            The evidenced email and source cannot be edited.
          </Alert>
          <TextField label="Email" value={contact.email} disabled />
          <TextField
            label="First name"
            value={value.firstName}
            onChange={(event) =>
              setValue((current) => ({
                ...current,
                firstName: event.target.value,
              }))
            }
          />
          <TextField
            label="Last name"
            value={value.lastName}
            onChange={(event) =>
              setValue((current) => ({
                ...current,
                lastName: event.target.value,
              }))
            }
          />
          <TextField
            label="Job title"
            value={value.jobTitle}
            onChange={(event) =>
              setValue((current) => ({
                ...current,
                jobTitle: event.target.value,
              }))
            }
          />
          <Autocomplete
            multiple
            options={subjects}
            value={subjects.filter((item) =>
              value.subjectIds.includes(item.id),
            )}
            getOptionLabel={(option) => option.name}
            onChange={(_event, items) =>
              setValue((current) => ({
                ...current,
                subjectIds: items.map((item) => item.id),
              }))
            }
            renderInput={(params) => <TextField {...params} label="Subjects" />}
          />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Evidence
            </Typography>
            <Typography>{contact.evidenceDetail}</Typography>
            <Link href={contact.evidenceUrl} target="_blank" rel="noreferrer">
              View public source
            </Link>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(value)}>
          Save contact
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function OutreachEnrichmentPage() {
  const clients = useApiClient();
  const api = clients.marketing;
  const user = useAuthenticationStore((state) => state.user);
  const isSuperuser = user?.info?.activeContext?.contextType === "superuser";
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedSchools, setSelectedSchools] = useState({});
  const [selectionMode, setSelectionMode] = useState("individual");
  const [excludedSchools, setExcludedSchools] = useState({});
  const [schoolFilters, setSchoolFilters] = useState(null);
  const [selectionSummary, setSelectionSummary] = useState(null);
  const [confirmingLaunch, setConfirmingLaunch] = useState(false);
  const [attemptEmailValidation, setAttemptEmailValidation] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [giasStatus, setGiasStatus] = useState(null);
  const [giasBusy, setGiasBusy] = useState(false);
  const [error, setError] = useState("");

  const fetchBatches = useCallback(
    ({ first, after }) => api.getOutreachEnrichmentBatches(first, after),
    [api],
  );
  const batches = usePagination(fetchBatches, 10);
  const loadGiasStatus = useCallback(async () => {
    setGiasStatus(await api.getGiasRefreshStatus());
  }, [api]);

  useEffect(() => {
    clients.core
      .getAllActiveSubjects()
      .then((result) => setSubjects(result.allActiveSubjects || []));
    loadGiasStatus().catch((requestError) => setError(requestError.message));
  }, [clients, loadGiasStatus]);

  useEffect(() => {
    if (!giasStatus?.currentJob) return undefined;
    const timer = setInterval(() => {
      loadGiasStatus().catch((requestError) => setError(requestError.message));
    }, 5000);
    return () => clearInterval(timer);
  }, [giasStatus?.currentJob, loadGiasStatus]);

  const refreshGias = async () => {
    setGiasBusy(true);
    try {
      const result = await api.refreshGiasSchoolData();
      enqueueSnackbar(result.message, {
        variant: result.success ? "success" : "warning",
      });
      await loadGiasStatus();
    } catch (requestError) {
      enqueueSnackbar(requestError.message, { variant: "error" });
    } finally {
      setGiasBusy(false);
    }
  };

  const selectedSchoolList = useMemo(
    () => Object.values(selectedSchools),
    [selectedSchools],
  );

  const selectionVariables = useMemo(() => {
    const subjectIds = selectedSubjects.map((item) => item.id);
    if (!subjectIds.length) return null;
    if (selectionMode === "all") {
      if (!schoolFilters) return null;
      return {
        schoolIds: null,
        schoolFilters,
        excludedSchoolIds: Object.keys(excludedSchools),
        subjectIds,
      };
    }
    if (!selectedSchoolList.length) return null;
    return {
      schoolIds: selectedSchoolList.map((item) => item.id),
      schoolFilters: null,
      excludedSchoolIds: [],
      subjectIds,
    };
  }, [
    excludedSchools,
    schoolFilters,
    selectedSchoolList,
    selectedSubjects,
    selectionMode,
  ]);

  useEffect(() => {
    if (!selectionVariables) {
      setSelectionSummary(null);
      return undefined;
    }
    setSelectionSummary(null);
    let active = true;
    const timer = setTimeout(() => {
      api
        .getOutreachEnrichmentSelectionSummary(selectionVariables)
        .then((result) => active && setSelectionSummary(result))
        .catch((requestError) => active && setError(requestError.message));
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [api, selectionVariables]);

  const handleSchoolFiltersChange = useCallback((filters) => {
    setSchoolFilters(filters);
  }, []);

  const toggleSchool = (school) => {
    if (selectionMode === "all") {
      setExcludedSchools((current) => {
        const next = { ...current };
        if (next[school.id]) delete next[school.id];
        else next[school.id] = school;
        return next;
      });
      return;
    }
    setSelectedSchools((current) => {
      const next = { ...current };
      if (next[school.id]) delete next[school.id];
      else next[school.id] = school;
      return next;
    });
  };

  const launch = async (includePreviouslyEnriched) => {
    if (!selectionVariables) return;
    setBusy(true);
    try {
      const result = await api.launchOutreachEnrichment({
        ...selectionVariables,
        includePreviouslyEnriched,
        attemptEmailValidation,
      });
      if (!result.success)
        throw new Error(
          result.errors?.map((item) => item.message).join(" ") ||
            result.message,
        );
      setSelectedSchools({});
      setExcludedSchools({});
      setSelectionMode("individual");
      setConfirmingLaunch(false);
      setAttemptEmailValidation(true);
      await batches.refetch();
      enqueueSnackbar(
        `${result.message} ${result.resolvedCount || 0} schools included.`,
        { variant: "success" },
      );
    } catch (requestError) {
      enqueueSnackbar(requestError.message, { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const runAction = async (action, refreshDetail = null) => {
    setBusy(true);
    try {
      const result = await action();
      if (!result.success)
        throw new Error(
          result.errors?.map((item) => item.message).join(" ") ||
            result.message,
        );
      enqueueSnackbar(result.message, { variant: "success" });
      if (refreshDetail) await refreshDetail();
      await batches.refetch();
      return true;
    } catch (requestError) {
      enqueueSnackbar(requestError.message, { variant: "error" });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const runBatchAgain = async (sourceBatch) => {
    setBusy(true);
    try {
      const schools = [];
      let after = null;
      let hasNextPage = true;
      while (hasNextPage) {
        const result = await api.getOutreachEnrichmentAttempts(sourceBatch.id, {
          first: 100,
          after,
        });
        schools.push(...result.edges.map(({ node }) => node.school));
        after = result.pageInfo.endCursor;
        hasNextPage = result.pageInfo.hasNextPage;
      }
      setSelectedSubjects(sourceBatch.subjects);
      setSelectedSchools(
        Object.fromEntries(schools.map((school) => [school.id, school])),
      );
      setExcludedSchools({});
      setSchoolFilters(null);
      setSelectionMode("individual");
      setAttemptEmailValidation(Boolean(sourceBatch.attemptEmailValidation));
      setConfirmingLaunch(true);
    } catch (requestError) {
      enqueueSnackbar(requestError.message, { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  if (!isSuperuser) return <Navigate to="/" replace />;

  return (
    <FruitifyContainer>
      <Stack spacing={3}>
        <Box>
          <Button
            component={RouterLink}
            to="/superuser/marketing/outreach"
            startIcon={<ArrowBack />}
          >
            Back to outreach
          </Button>
          <Typography variant="h4">Outreach contact enrichment</Typography>
          <Typography color="text.secondary">
            Find publicly evidenced, named school contacts and manage them
            before outreach.
          </Typography>
        </Box>
        {error && <Alert severity="error">{error}</Alert>}
        <Card variant="outlined">
          <CardContent>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ sm: "center" }}
              spacing={2}
            >
              <Box>
                <Typography variant="h6">GIAS school website data</Typography>
                <Typography color="text.secondary">
                  Refresh public school website information before running
                  contact enrichment.
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  {giasStatus?.lastCompletedAt
                    ? `Last updated ${new Date(giasStatus.lastCompletedAt).toLocaleString()}`
                    : "No completed refresh recorded"}
                </Typography>
                {giasStatus?.currentJob && (
                  <Typography variant="body2" color="primary" mt={0.5}>
                    Refresh in progress: {giasStatus.currentJob.progress || 0}%
                  </Typography>
                )}
              </Box>
              <Button
                variant="outlined"
                startIcon={<CloudDownload />}
                disabled={giasBusy || Boolean(giasStatus?.currentJob)}
                onClick={refreshGias}
              >
                Refresh GIAS data
              </Button>
            </Stack>
          </CardContent>
        </Card>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">Create enrichment batch</Typography>
              <Alert severity="info">
                Schools with approved subscriptions are excluded because they
                are already Fruitify customers.
              </Alert>
              <Autocomplete
                multiple
                options={subjects}
                value={selectedSubjects}
                getOptionLabel={(option) => option.name}
                onChange={(_event, value) =>
                  setSelectedSubjects(value.slice(0, 10))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Target subjects" />
                )}
              />
              <SchoolSearchAutocomplete
                showResults
                phaseFilter
                localAuthorityFilter
                subscriptionSwitch
                onFiltersChange={handleSchoolFiltersChange}
              >
                {({ options, page, pageInfo, handlePageChange }) => (
                  <>
                    <Box
                      sx={{
                        maxHeight: { xs: 420, md: 560 },
                        overflowY: "auto",
                        pr: 1,
                      }}
                    >
                      <Stack spacing={1} mt={1}>
                        {options.map((school) => (
                          <Card key={school.id} variant="outlined">
                            <FormControlLabel
                              sx={{
                                m: 0,
                                p: 1.5,
                                width: "100%",
                                "& .MuiFormControlLabel-label": { flex: 1 },
                              }}
                              control={
                                <Checkbox
                                  checked={
                                    selectionMode === "all"
                                      ? !excludedSchools[school.id]
                                      : Boolean(selectedSchools[school.id])
                                  }
                                  onChange={() => toggleSchool(school)}
                                />
                              }
                              label={
                                <Stack
                                  direction={{ xs: "column", md: "row" }}
                                  justifyContent="space-between"
                                  alignItems={{ md: "center" }}
                                  gap={1}
                                  width="100%"
                                >
                                  <Box>
                                    <Typography fontWeight={600}>
                                      {school.name}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {[school.addressLine1, school.town]
                                        .filter(Boolean)
                                        .join(", ")}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Local authority:{" "}
                                      {school.localAuthority?.name ||
                                        school.localAuthority?.code ||
                                        "Not provided"}
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: {
                                        xs: "1fr",
                                        sm: "150px 120px 250px",
                                      },
                                      gap: 1,
                                      minWidth: { md: 536 },
                                    }}
                                  >
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={school.phaseOfEducationDisplay}
                                    />
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={`${school.identifiedContactsCount || 0} contacts`}
                                    />
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={
                                        school.lastEnrichedAt
                                          ? `Last enriched ${new Date(school.lastEnrichedAt).toLocaleString()}`
                                          : "Never enriched"
                                      }
                                    />
                                  </Box>
                                </Stack>
                              }
                            />
                          </Card>
                        ))}
                      </Stack>
                    </Box>
                    <Pagination
                      count={pageInfo.hasNextPage ? page + 1 : page}
                      page={page}
                      onChange={handlePageChange}
                    />
                  </>
                )}
              </SchoolSearchAutocomplete>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button
                  variant={selectionMode === "all" ? "contained" : "outlined"}
                  onClick={() => {
                    setSelectionMode("all");
                    setSelectedSchools({});
                    setExcludedSchools({});
                  }}
                >
                  Select all matching schools
                </Button>
                <Button
                  disabled={selectionMode === "individual"}
                  onClick={() => {
                    setSelectionMode("individual");
                    setExcludedSchools({});
                  }}
                >
                  Choose schools individually
                </Button>
              </Stack>
              <Typography variant="body2">
                {selectionMode === "all"
                  ? `All matching schools selected${Object.keys(excludedSchools).length ? `, excluding ${Object.keys(excludedSchools).length}` : ""}`
                  : `${selectedSchoolList.length} schools selected`}
              </Typography>
              {selectionMode === "individual" && (
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {selectedSchoolList.map((school) => (
                    <Chip
                      key={school.id}
                      label={school.name}
                      onDelete={() => toggleSchool(school)}
                    />
                  ))}
                </Stack>
              )}
              {selectionSummary && (
                <Stack direction="row" gap={1} flexWrap="wrap">
                  <Chip label={`${selectionSummary.total} matching`} />
                  <Chip
                    label={`${selectionSummary.neverAttempted} never enriched`}
                  />
                  <Chip
                    label={`${selectionSummary.successful} contacts found`}
                  />
                  <Chip label={`${selectionSummary.noLead} no contacts`} />
                  <Chip label={`${selectionSummary.failed} failed`} />
                  {selectionSummary.active > 0 && (
                    <Chip
                      color="warning"
                      label={`${selectionSummary.active} already in progress`}
                    />
                  )}
                </Stack>
              )}
              <Button
                variant="contained"
                startIcon={<PlayArrow />}
                disabled={busy || !selectionVariables || !selectionSummary}
                onClick={() => setConfirmingLaunch(true)}
              >
                Start enrichment
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Box>
          <Typography variant="h5" gutterBottom>
            Enrichment batches
          </Typography>
          {batches.loading && <CircularProgress size={24} />}
          {batches.error && (
            <Alert severity="error">{batches.error.message}</Alert>
          )}
          {!batches.loading && batches.options.length === 0 && (
            <Alert severity="info">
              No enrichment batches have been run yet.
            </Alert>
          )}
          <Stack spacing={2}>
            {batches.options.map((item) => (
              <BatchCard
                key={item.id}
                batch={item}
                api={api}
                busy={busy}
                onAction={runAction}
                onEdit={(contact, refresh) => setEditing({ contact, refresh })}
                onRunAgain={runBatchAgain}
                refreshBatches={batches.refetch}
              />
            ))}
          </Stack>
          {batches.options.length > 0 && (
            <Box mt={2}>
              <PageControl {...batches} onChange={batches.handlePageChange} />
            </Box>
          )}
        </Box>
      </Stack>
      {editing && (
        <ContactDialog
          contact={editing.contact}
          subjects={subjects}
          onClose={() => setEditing(null)}
          onSave={async (value) => {
            const saved = await runAction(
              () =>
                api.updateOutreachContact({
                  contactId: editing.contact.id,
                  ...value,
                }),
              editing.refresh,
            );
            if (saved) setEditing(null);
          }}
        />
      )}
      <Dialog
        open={confirmingLaunch}
        onClose={() => !busy && setConfirmingLaunch(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Confirm enrichment batch</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Typography>
              Choose whether to repeat completed enrichment for the selected
              subjects. Schools already pending or processing are excluded.
            </Typography>
            {selectionSummary && (
              <Alert severity="info">
                {selectionSummary.availableTotal} schools are available;{" "}
                {selectionSummary.notAlreadyEnriched} have not already completed
                enrichment for this exact subject selection. Estimated cost: $
                {Number(
                  selectionSummary.estimatedAvailableCostUsd || 0,
                ).toFixed(2)}{" "}
                for all, or $
                {Number(
                  selectionSummary.estimatedNotAlreadyEnrichedCostUsd || 0,
                ).toFixed(2)}{" "}
                for only those not already enriched.
              </Alert>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={attemptEmailValidation}
                  onChange={(event) =>
                    setAttemptEmailValidation(event.target.checked)
                  }
                />
              }
              label="Attempt email validation automatically"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap" }}>
          <Button disabled={busy} onClick={() => setConfirmingLaunch(false)}>
            Cancel
          </Button>
          <Button
            disabled={busy || !selectionSummary?.notAlreadyEnriched}
            onClick={() => launch(false)}
          >
            Only not already enriched (
            {selectionSummary?.notAlreadyEnriched || 0})
          </Button>
          <Button
            variant="contained"
            disabled={busy || !selectionSummary?.availableTotal}
            onClick={() => launch(true)}
          >
            All in selection ({selectionSummary?.availableTotal || 0})
          </Button>
        </DialogActions>
      </Dialog>
    </FruitifyContainer>
  );
}
